import React, { useEffect, useRef, useState } from "react"
import { useParams, Link } from "react-router-dom"
import { Editor } from "@tinymce/tinymce-react"
import SplitPane from "react-split-pane"
import Button from "react-bootstrap/Button"
import Modal from "react-bootstrap/Modal"
import Form from "react-bootstrap/Form"
import API from "./../../Util/API"
import {
	FaTools,
	FaHeading,
	FaAlignLeft,
	FaImage,
	FaSave,
	FaFilePdf,
	FaFont,
	FaTrash,
	FaGlobe,
	FaEyeSlash,
	FaArrowLeft,
	FaTimes,
	FaCheckCircle,
} from "react-icons/fa"
import "./BlogPostEditor.css"

const previewStyle = {
	overflowY: "scroll",
	blockSize: "100vh",
}

export function BlogPostEditor() {
	const { category, id } = useParams()
	const postDataRef = useRef(null)
	const [editorHtml, setEditorHtml] = useState(null)
	const editorHtmlRef = useRef(editorHtml)
	const [imageCover, setImageCover] = useState(null)
	const [isFabOpen, setIsFabOpen] = useState(true)
	const [notification, setNotification] = useState(null)
	const notificationTimeoutRef = useRef(null)

	const showNotification = (message, variant = "success") => {
		if (notificationTimeoutRef.current) {
			clearTimeout(notificationTimeoutRef.current)
		}
		setNotification({ message, variant })
		notificationTimeoutRef.current = setTimeout(() => {
			setNotification(null)
		}, 3000)
	}

	const path = category
	const pdfNameRef = useRef(null)
	const pdfRef = useRef(null)
	const [pdfUrl, setPdfUrl] = useState(null)

	const [showTitleModal, setShowTitleModal] = useState(false)
	const handleCloseTitleModal = () => setShowTitleModal(false)
	const handleShowTitleModal = () => setShowTitleModal(true)

	const [showAbstractModal, setShowAbstractModal] = useState(false)
	const handleCloseAbstractModal = () => setShowAbstractModal(false)
	const handleShowAbstractModal = () => setShowAbstractModal(true)

	const [showImageModal, setShowImageModal] = useState(false)
	const handleCloseImageModal = () => setShowImageModal(false)
	const handleShowImageModal = () => setShowImageModal(true)

	const titleFormValueRef = useRef(null),
		onTitleFormInput = ({ target: { value } }) => (titleFormValueRef.current = value)

	const abstractFormValueRef = useRef(null),
		onAbstractFormInput = ({ target: { value } }) => (abstractFormValueRef.current = value)

	const [showPdfMode, setShowPdfMode] = useState(false)
	const [showTextEditorMode, setShowTextEditorMode] = useState(false)

	const api = API()

	useEffect(() => {
		api
			.get(`${category}/${id}`)
			.then((response) => {
				if (response.mode == "pdf") {
					setShowPdfMode(true)
					setShowTextEditorMode(false)
				} else {
					setShowPdfMode(false)
					setShowTextEditorMode(true)
				}

				postDataRef.current = response
				setImageCover(response.imageCover)
				titleFormValueRef.current = response.title
				abstractFormValueRef.current = response.abstract
				pdfNameRef.current = response.pdfName
				pdfRef.current = response.pdf

				if (response.pdf) setPdfUrl(pdfUrlFromBlobString(response.pdf))

				setEditorHtml(response.content)
				editorHtmlRef.current = response.content
			})
			.catch((error) => {
				console.error(error)
			})
	}, [])

	function saveLoadedImageBlob(image) {
		var reader = new FileReader()

		reader.onload = function () {
			setImageCover(reader.result)
		}

		reader.readAsDataURL(image)
	}

	function saveTitle() {
		postDataRef.current.title = titleFormValueRef.current
		setShowTitleModal(false)

		api
			.put(`${path}/${id}`, postDataRef.current)
			.then(() => {
				showNotification("Título atualizado com sucesso!")
			})
			.catch((error) => {
				console.error(error)
			})
	}

	function saveAbstract() {
		postDataRef.current.abstract = abstractFormValueRef.current
		setShowAbstractModal(false)

		api
			.put(`${path}/${id}`, postDataRef.current)
			.then(() => {
				showNotification("Resumo atualizado com sucesso!")
			})
			.catch((error) => {
				console.error(error)
			})
	}

	function saveImageCover() {
		postDataRef.current.imageCover = imageCover
		setShowImageModal(false)

		api
			.put(`${path}/${id}`, postDataRef.current)
			.then(() => {
				showNotification(imageCover ? "Imagem de capa atualizada com sucesso!" : "Imagem de capa removida!")
			})
			.catch((error) => {
				console.error(error)
			})
	}

	function removeImageCover() {
		setImageCover("")
		postDataRef.current.imageCover = ""
		setShowImageModal(false)

		api
			.put(`${path}/${id}`, postDataRef.current)
			.then(() => {
				showNotification("Imagem de capa removida com sucesso!")
			})
			.catch((error) => {
				console.error(error)
			})
	}

	function saveContent(isAutoSave = false) {
		if (showPdfMode) {
			postDataRef.current.pdfName = pdfNameRef.current
			postDataRef.current.pdf = pdfRef.current
			postDataRef.current.mode = "pdf"
		}

		if (showTextEditorMode) {
			postDataRef.current.content = editorHtml
			postDataRef.current.pdfName = ""
			postDataRef.current.pdf = ""
			postDataRef.current.mode = "text"
		}

		api
			.put(`${path}/${id}`, postDataRef.current)
			.then(() => {
				if (!isAutoSave) {
					showNotification("Alterações salvas com sucesso!")
				}
			})
			.catch((error) => {
				console.error(error)
			})
	}

	function removePDF() {
		pdfNameRef.current = ""
		pdfRef.current = ""
		setPdfUrl("")
		saveContent(true)
		showNotification("PDF removido com sucesso!")
	}

	function publishData() {
		postDataRef.current.published = true

		api
			.put(`${path}/${id}`, postDataRef.current)
			.then(() => {
				showNotification("Artigo publicado com sucesso!")
			})
			.catch((error) => {
				console.error(error)
			})
	}

	function unpublishData() {
		postDataRef.current.published = false

		api
			.put(`${path}/${id}`, postDataRef.current)
			.then(() => {
				showNotification("Artigo retirado do ar com sucesso!", "warning")
			})
			.catch((error) => {
				console.error(error)
			})
	}

	function uploadPDF(event) {
		if (event.target.files.length > 0) {
			let file = event.target.files[0]

			let reader = new FileReader()

			reader.onload = function () {
				let base64String = reader.result
				pdfNameRef.current = file.name
				pdfRef.current = base64String
				setPdfUrl(pdfUrlFromBlobString(base64String))
				showNotification("Arquivo PDF carregado com sucesso!")
			}

			reader.readAsDataURL(file)
		}
	}

	function pdfUrlFromBlobString(blobString) {
		let base64String = blobString.replace("data:application/pdf;base64,", "")
		let binaryString = window.atob(base64String)
		let binaryLen = binaryString.length
		let bytes = new Uint8Array(binaryLen)

		for (let i = 0; i < binaryLen; i++) {
			let ascii = binaryString.charCodeAt(i)
			bytes[i] = ascii
		}

		let blob = new Blob([bytes], { type: "application/pdf" })
		return URL.createObjectURL(blob)
	}

	function useInterval(callback, delay) {
		const savedCallback = useRef()

		// Remember the latest callback.
		useEffect(() => {
			savedCallback.current = callback
		}, [callback])

		// Set up the interval.
		useEffect(() => {
			function tick() {
				savedCallback.current()
			}
			if (delay !== null) {
				let id = setInterval(tick, delay)
				return () => clearInterval(id)
			}
		}, [delay])
	}

	useInterval(() => {
		saveContent(true)
	}, 10000)

	function file_picker_callback(cb, value, meta) {
		var input = document.createElement("input")
		input.setAttribute("type", "file")
		input.setAttribute("accept", "image/*")

		input.onchange = function () {
			var file = this.files[0]

			var reader = new FileReader()
			reader.onload = function () {
				var id = "blobid" + new Date().getTime()
				var blobCache = tinymce.activeEditor.editorUpload.blobCache
				var base64 = reader.result.split(",")[1]
				var blobInfo = blobCache.create(id, file, base64)
				blobCache.add(blobInfo)

				/* call the callback and populate the Title field with the file name */
				cb(blobInfo.blobUri(), { title: file.name })
			}
			reader.readAsDataURL(file)
		}

		input.click()
	}

	function setEditMode(editMode) {
		if (editMode === "pdf") {
			setShowPdfMode(true)
			setShowTextEditorMode(false)
			showNotification("Modo PDF ativado!")
		}
		if (editMode === "text") {
			setShowPdfMode(false)
			setShowTextEditorMode(true)
			showNotification("Modo Editor de Texto ativado!")
		}
	}

	return (
		<>
			{/* Operation Success Toast Notification */}
			{notification && (
				<div
					className="editor-notification-toast position-fixed top-0 start-50 translate-middle-x mt-4 shadow-lg rounded-pill px-4 py-2 d-flex align-items-center gap-2 bg-dark text-white"
					style={{ zIndex: 9999 }}
				>
					<FaCheckCircle className={notification.variant === "warning" ? "text-warning" : "text-success"} size={18} />
					<span className="fw-medium small">{notification.message}</span>
				</div>
			)}

			{/* Floating Action Button (FAB) Menu for Editor Actions */}
			<div className="editor-fab-container">
				{isFabOpen && (
					<div className="editor-fab-menu shadow-lg bg-white rounded-4 border p-3 mb-2 d-flex flex-column gap-2">
						<div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-1">
							<span className="fw-bold small text-dark d-flex align-items-center gap-1">
								<FaTools className="text-primary" /> Ações do Artigo
							</span>
							<Link to="/admin" className="btn btn-sm btn-outline-secondary py-0 px-2" title="Voltar ao Painel">
								<FaArrowLeft size={10} /> Painel
							</Link>
						</div>

						<button className="btn btn-sm btn-outline-dark text-start d-flex align-items-center gap-2" type="button" onClick={handleShowTitleModal}>
							<FaHeading className="text-primary" /> Editar Título
						</button>

						<button
							className="btn btn-sm btn-outline-dark text-start d-flex align-items-center gap-2"
							type="button"
							onClick={handleShowAbstractModal}
						>
							<FaAlignLeft className="text-info" /> Editar Resumo
						</button>

						<button className="btn btn-sm btn-outline-dark text-start d-flex align-items-center gap-2" type="button" onClick={handleShowImageModal}>
							<FaImage className="text-warning" /> Imagem de Capa
						</button>

						{category === "publications" && (
							<div className="border-top pt-2 mt-1 d-flex flex-column gap-2">
								<div className="btn-group btn-group-sm w-100">
									<button className={`btn ${showPdfMode ? "btn-primary" : "btn-outline-primary"}`} type="button" onClick={() => setEditMode("pdf")}>
										<FaFilePdf /> PDF
									</button>
									<button
										className={`btn ${showTextEditorMode ? "btn-primary" : "btn-outline-primary"}`}
										type="button"
										onClick={() => setEditMode("text")}
									>
										<FaFont /> Texto
									</button>
								</div>
								<button className="btn btn-sm btn-outline-danger text-start d-flex align-items-center gap-2" type="button" onClick={removePDF}>
									<FaTrash /> Remover PDF
								</button>
							</div>
						)}

						<div className="border-top pt-2 mt-1 d-flex flex-column gap-2">
							<button
								className="btn btn-sm btn-primary d-flex align-items-center justify-content-center gap-2 shadow-sm"
								type="button"
								onClick={() => saveContent()}
							>
								<FaSave /> Salvar Alterações
							</button>

							<div className="d-flex gap-2">
								<button
									className="btn btn-sm btn-success flex-grow-1 d-flex align-items-center justify-content-center gap-1"
									type="button"
									onClick={publishData}
								>
									<FaGlobe /> Publicar
								</button>
								<button
									className="btn btn-sm btn-outline-warning flex-grow-1 d-flex align-items-center justify-content-center gap-1"
									type="button"
									onClick={unpublishData}
								>
									<FaEyeSlash /> Ocultar
								</button>
							</div>
						</div>
					</div>
				)}

				<button
					type="button"
					className="editor-fab-trigger btn btn-primary rounded-circle shadow-lg d-flex align-items-center justify-content-center text-white"
					onClick={() => setIsFabOpen(!isFabOpen)}
					title={isFabOpen ? "Minimizar menu" : "Menu de Ações do Artigo"}
					style={{ width: "56px", height: "56px" }}
				>
					{isFabOpen ? <FaTimes size={22} /> : <FaTools size={22} />}
				</button>
			</div>

			{showTextEditorMode && (
				<SplitPane className="splitter-pane" split="vertical" minSize={10} defaultSize={typeof window !== "undefined" ? window.innerWidth / 2 : 600}>
					<div className="editor-container">
						<Editor
							id="tinyMCE"
							init={{
								selector: "textarea",
								plugins: [
									"a11ychecker",
									"advlist",
									"advcode",
									"advtable",
									"autolink",
									"checklist",
									"export",
									"lists",
									"link",
									"image",
									"charmap",
									"preview",
									"anchor",
									"searchreplace",
									"visualblocks",
									"powerpaste",
									"fullscreen",
									"formatpainter",
									"insertdatetime",
									"table",
									"help",
									"wordcount",
								],
								toolbar:
									"undo redo | casechange blocks | bold italic backcolor | " +
									"alignleft aligncenter alignright alignjustify | " +
									"bullist numlist checklist outdent indent | removeformat | a11ycheck code table help",
								content_style: "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
								language: "pt_BR",
								image_title: true,
								automatic_uploads: true,
								file_picker_callback: file_picker_callback,
							}}
							onInit={(evt, editor) => {
								editor.on("Paste Change input Undo Redo", () => {
									setEditorHtml(editor.getContent())
								})
							}}
							initialValue={editorHtmlRef.current}
						/>
					</div>
					<div dangerouslySetInnerHTML={{ __html: editorHtml }} style={previewStyle} />
				</SplitPane>
			)}

			{showPdfMode && (
				<div className="d-flex justify-content-center align-items-center" style={{ height: "84.7vh", width: "99.3vw" }}>
					{!pdfUrl ? (
						<input type="file" id="pdfFileInput" name="pdfFile" accept="application/pdf" onChange={(event) => uploadPDF(event)} />
					) : (
						<iframe src={pdfUrl} style={{ border: "none", width: "100%", height: "100%" }}></iframe>
					)}
				</div>
			)}

			<Modal show={showTitleModal} onHide={handleCloseTitleModal} backdrop="static" keyboard={false}>
				<Modal.Header className="px-4" closeButton>
					<Modal.Title className="ms-auto">Editar Título</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<Form>
						<Form.Group className="mb-3" controlId="titleForm.ControlInput">
							<Form.Label>Informe um título:</Form.Label>
							<Form.Control type="text" placeholder="Título" onChange={onTitleFormInput} defaultValue={titleFormValueRef.current} autoFocus />
						</Form.Group>
					</Form>
				</Modal.Body>
				<Modal.Footer>
					<Button variant="primary" onClick={() => saveTitle()}>
						Salvar
					</Button>
					<Button variant="secondary" onClick={handleCloseTitleModal}>
						Fechar
					</Button>
				</Modal.Footer>
			</Modal>

			<Modal id="resumeModal" size="lg" show={showAbstractModal} onHide={handleCloseAbstractModal} backdrop="static" keyboard={false}>
				<Modal.Header closeButton>
					<Modal.Title>Editar Resumo</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<Form>
						<Form.Group className="mb-3" controlId="abstractForm.ControlTextarea">
							<Form.Label>Informe um resumo para o post:</Form.Label>
							<Form.Control
								as="textarea"
								rows={5}
								placeholder="Resumo"
								onChange={onAbstractFormInput}
								defaultValue={abstractFormValueRef.current}
								autoFocus
							/>
						</Form.Group>
					</Form>
				</Modal.Body>
				<Modal.Footer>
					<Button variant="primary" onClick={() => saveAbstract()}>
						Salvar
					</Button>
					<Button variant="secondary" onClick={handleCloseAbstractModal}>
						Fechar
					</Button>
				</Modal.Footer>
			</Modal>

			<Modal show={showImageModal} onHide={handleCloseImageModal} size="lg" centered backdrop="static" keyboard={false}>
				<Modal.Header closeButton>
					<Modal.Title className="fw-bold fs-5 text-dark">Editar Imagem de Preview</Modal.Title>
				</Modal.Header>
				<Modal.Body className="p-4">
					{imageCover && imageCover !== "" ? (
						<div
							className="text-center mb-3 p-3 bg-light rounded-3 border d-flex align-items-center justify-content-center"
							style={{ minHeight: "200px" }}
						>
							<img
								src={imageCover}
								alt="Preview da capa"
								className="img-fluid rounded shadow-sm"
								style={{ maxHeight: "380px", maxWidth: "100%", width: "auto", height: "auto", objectFit: "contain" }}
							/>
						</div>
					) : (
						<div className="text-center py-4 mb-3 bg-light rounded-3 border text-muted d-flex flex-column align-items-center justify-content-center">
							<FaImage size={48} className="opacity-25 mb-2" />
							<p className="small mb-0">Nenhuma imagem de capa selecionada.</p>
						</div>
					)}

					<Form.Group className="mb-3">
						<Form.Label className="fw-semibold small text-dark">Selecionar nova imagem:</Form.Label>
						<Form.Control
							type="file"
							accept="image/*"
							onChange={(e) => {
								if (e.target.files && e.target.files[0]) {
									saveLoadedImageBlob(e.target.files[0])
								}
							}}
							autoFocus
						/>
					</Form.Group>

					{imageCover && imageCover !== "" && (
						<Button variant="outline-danger" size="sm" className="d-inline-flex align-items-center gap-2" onClick={() => removeImageCover()}>
							<FaTrash size={12} /> Remover imagem
						</Button>
					)}
				</Modal.Body>
				<Modal.Footer>
					<Button variant="primary" onClick={() => saveImageCover()}>
						Salvar
					</Button>
					<Button variant="secondary" onClick={handleCloseImageModal}>
						Fechar
					</Button>
				</Modal.Footer>
			</Modal>
		</>
	)
}
